import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { PasswordInput } from "../../components/PasswordInput";

export const AdminLogin = () => {
  const { user, login, loginOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  // Second-factor state — populated after step 1 succeeds.
  const [otpTicket, setOtpTicket] = useState(null);
  const [otp, setOtp] = useState("");

  if (user && user !== false) return <Navigate to="/admin" replace />;

  const readableError = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    return "Identifiants invalides";
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      const result = await login(email, password);
      if (result?.requires_otp) {
        setOtpTicket(result.otp_ticket);
        toast.info("Entrez le code de votre application d'authentification");
        return;
      }
      toast.success("Bienvenue");
      navigate("/admin");
    } catch (err) {
      toast.error(readableError(err));
    } finally {
      setPending(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      await loginOtp({ email, otp: otp.trim(), otp_ticket: otpTicket });
      toast.success("Bienvenue");
      navigate("/admin");
    } catch (err) {
      toast.error(readableError(err));
    } finally {
      setPending(false);
    }
  };

  const backToPassword = () => {
    setOtpTicket(null);
    setOtp("");
  };

  return (
    <div className="min-h-screen bg-[var(--bg-cream)] flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <span className="brand-mark text-3xl md:text-4xl">ROUGEPREMIERE</span>
        </Link>
        {!otpTicket ? (
          <form
            onSubmit={submitPassword}
            className="bg-white border border-[var(--line)] rounded-md p-8 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[var(--red)] mb-1">
              <Lock className="h-3 w-3" /> Espace rédaction
            </div>
            <h1 className="font-display text-3xl mt-1 mb-6">Connexion</h1>
            <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-1.5">
              Email
            </label>
            <input
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full mb-4 px-3 py-2.5 rounded border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)]"
            />
            <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-1.5">
              Mot de passe
            </label>
            <PasswordInput
              testId="admin-login-password"
              value={password}
              onChange={setPassword}
              required
              autoComplete="current-password"
              className="mb-6"
            />
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={pending}
              className="btn-red w-full justify-center"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {pending ? "Connexion…" : "Se connecter"}
            </button>
            <div className="text-center mt-6 text-xs text-neutral-500">
              <Link to="/" className="hover:text-[var(--red)]">
                ← Retour au site
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={submitOtp}
            className="bg-white border border-[var(--line)] rounded-md p-8 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[var(--red)] mb-1">
              <ShieldCheck className="h-3 w-3" /> Vérification en 2 étapes
            </div>
            <h1 className="font-display text-3xl mt-1 mb-3">Code de sécurité</h1>
            <p className="text-sm text-neutral-500 mb-6">
              Ouvrez votre application d'authentification (Google Authenticator, 1Password…) et saisissez le code à 6 chiffres, ou l'un de vos codes de secours.
            </p>
            <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-1.5">
              Code
            </label>
            <input
              data-testid="admin-login-otp"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              placeholder="123456 ou xxxx-xxxx"
              className="w-full mb-6 px-3 py-2.5 rounded border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)] font-mono tracking-widest text-center text-lg"
            />
            <button
              data-testid="admin-login-otp-submit"
              type="submit"
              disabled={pending}
              className="btn-red w-full justify-center"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {pending ? "Vérification…" : "Vérifier"}
            </button>
            <button
              type="button"
              onClick={backToPassword}
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[var(--red)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Revenir en arrière
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
